import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Select, Row, Col, Spin, Space, Empty, Progress, Tag, Segmented } from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Cell, LabelList,
} from "recharts";
import {
    CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
    ClockCircleOutlined, RiseOutlined, ExclamationCircleOutlined,
    BankOutlined, FilterOutlined, LogoutOutlined, AppstoreOutlined,
    CoffeeOutlined, GiftOutlined, BarChartOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

export default function AttendanceReport() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");
    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState("ALL");
    const [vizMode, setVizMode] = useState("rate"); // "rate" | "count"

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        const q = organisationId !== "ALL" ? `?organisation=${organisationId}` : "";
        axios.get(`${backUrl}/api/emp/departments/${q}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            setDepartments(res.data.results || res.data || []);
            setDepartmentId("ALL");
        }).catch(() => setDepartments([]));
    }, [organisationId, backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisationId !== "ALL") params.organisation_id = organisationId;
        if (departmentId !== "ALL") params.department_id = departmentId;

        axios.get(`${backUrl}/api/reports/attendance-summary/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setData(res.data || null))
          .catch(() => setData(null))
          .finally(() => setLoading(false));
    }, [organisationId, departmentId, month, year, backUrl, authData?.access]);

    const presentPct     = Number(data?.present_percentage || 0);
    const absentPct      = Number(data?.absent_percentage  || 0);
    const leavePct       = Number(data?.leave_percentage   || 0);
    const latePct        = Number(data?.late_percentage    || 0);
    const lateDays       = Number(data?.late_days ?? data?.late_count ?? 0);
    const lateInDays     = Number(data?.late_in_days      || 0);
    const graceLateDays  = Number(data?.grace_late_days   || 0);
    const earlyOutDays   = Number(data?.early_out_days    || 0);
    const graceEarlyDays = Number(data?.grace_early_days  || 0);
    const nlbDays        = Number(data?.nlb_days ?? data?.nlb_count ?? 0);
    const otHours        = Number(data?.overtime_hours     || 0);
    const weekoffDays    = Number(data?.weekoff_days ?? data?.weekoff_count ?? 0);
    const weekoffPct     = Number(data?.weekoff_percentage || 0);
    const compoffDays    = Number(data?.compoff_days ?? data?.compoff_count ?? 0);
    const compoffPct     = Number(data?.compoff_percentage || 0);
    const presentDays    = Number(data?.present_days ?? data?.present_count ?? 0);
    const absentDays     = Number(data?.absent_days  ?? data?.absent_count  ?? 0);
    const leaveDays      = Number(data?.leave_days   ?? data?.leave_count   ?? 0);

    const kpis = [
        {
            label: "Present Rate",
            val: `${presentPct}%`,
            icon: <CheckCircleOutlined />,
            color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0",
            progress: presentPct, showProgress: true,
            subTags: [
                { label: `${presentDays.toLocaleString()} Days`, color: "#10b981" }
            ],
            subText: "Overall Attendance"
        },
        {
            label: "Absent Rate",
            val: `${absentPct}%`,
            icon: <CloseCircleOutlined />,
            color: "#ef4444", bg: "#fff1f2", border: "#fecaca",
            progress: absentPct, showProgress: true,
            subTags: [
                { label: `${absentDays.toLocaleString()} Days`, color: "#ef4444" }
            ],
            subText: "Unexcused Absence"
        },
        {
            label: "Weekoff Days",
            val: weekoffDays.toLocaleString(),
            icon: <CoffeeOutlined />,
            color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd",
            showProgress: false,
            subTags: [
                { label: `${weekoffPct}%`, color: "#0284c7" },
                { label: "Weekly Off", color: "#0369a1" }
            ]
        },
        {
            label: "Compoff Days",
            val: compoffDays.toLocaleString(),
            icon: <GiftOutlined />,
            color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4",
            showProgress: false,
            subTags: [
                { label: `${compoffPct}%`, color: "#0d9488" },
                { label: "Compensatory", color: "#0f766e" }
            ]
        },
        {
            label: "Late Days",
            val: lateDays.toLocaleString(),
            icon: <ClockCircleOutlined />,
            color: "#f59e0b", bg: "#fffbeb", border: "#fde68a",
            showProgress: false,
            subTags: [
                { label: `Late: ${lateInDays}`, color: "#d97706" },
                { label: `Grace: ${graceLateDays}`, color: "#b45309" }
            ]
        },
        {
            label: "Early Out Days",
            val: (earlyOutDays + graceEarlyDays).toLocaleString(),
            icon: <LogoutOutlined />,
            color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe",
            showProgress: false,
            subTags: [
                { label: `Early: ${earlyOutDays}`, color: "#7c3aed" },
                { label: `Grace: ${graceEarlyDays}`, color: "#6d28d9" }
            ]
        },
        {
            label: "NLB Days",
            val: nlbDays,
            icon: <ExclamationCircleOutlined />,
            color: "#bf4406", bg: "#fff1f2", border: "#fecaca",
            showProgress: false,
            subText: "No Pay / LOP"
        },
        {
            label: "OT Hours",
            val: `${otHours}h`,
            icon: <RiseOutlined />,
            color: "#0e497b", bg: "#eff6ff", border: "#bfdbfe",
            showProgress: false,
            subText: "Overtime Work"
        },
    ];

    const chartData = data ? [
        {
            name: vizMode === "count" ? "Present" : "Present Rate",
            shortName: "Present",
            count: presentDays,
            rate: presentPct,
            value: vizMode === "count" ? presentDays : presentPct,
            fill: "#10b981",
            label: vizMode === "count" ? Number(presentDays).toLocaleString() : `${presentPct}%`,
        },
        {
            name: vizMode === "count" ? "Absent" : "Absent Rate",
            shortName: "Absent",
            count: absentDays,
            rate: absentPct,
            value: vizMode === "count" ? absentDays : absentPct,
            fill: "#ef4444",
            label: vizMode === "count" ? Number(absentDays).toLocaleString() : `${absentPct}%`,
        },
        {
            name: vizMode === "count" ? "Week Off" : "Week Off",
            shortName: "Week Off",
            count: weekoffDays,
            rate: weekoffPct,
            value: vizMode === "count" ? weekoffDays : weekoffPct,
            fill: "#0284c7",
            label: vizMode === "count" ? Number(weekoffDays).toLocaleString() : `${weekoffPct}%`,
        },
        {
            name: vizMode === "count" ? "Comp Off" : "Comp Off",
            shortName: "Comp Off",
            count: compoffDays,
            rate: compoffPct,
            value: vizMode === "count" ? compoffDays : compoffPct,
            fill: "#0d9488",
            label: vizMode === "count" ? Number(compoffDays).toLocaleString() : `${compoffPct}%`,
        },
        {
            name: vizMode === "count" ? "Late" : "Late Rate",
            shortName: "Late",
            count: lateDays,
            rate: latePct,
            value: vizMode === "count" ? lateDays : latePct,
            fill: "#f59e0b",
            label: vizMode === "count" ? Number(lateDays).toLocaleString() : `${latePct}%`,
        },
        {
            name: vizMode === "count" ? "On Leave" : "On Leave",
            shortName: "Leave",
            count: leaveDays,
            rate: leavePct,
            value: vizMode === "count" ? leaveDays : leavePct,
            fill: "#CB00CC",
            label: vizMode === "count" ? Number(leaveDays).toLocaleString() : `${leavePct}%`,
        },
    ] : [];

    return (
        <div style={{
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            position: "relative", height: "100%",
        }}>
            <div style={{ height: 3.5, background: "linear-gradient(90deg, #0e497b, #10b981)", borderTopLeftRadius: 16, borderTopRightRadius: 16 }} />
            <div style={{ padding: "16px 18px" }}>
                {/* Section Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: "linear-gradient(135deg, #0e497b 0%, #1e40af 100%)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff", boxShadow: "0 2px 8px rgba(14,73,123,0.25)", flexShrink: 0
                        }}>
                            <CalendarOutlined style={{ fontSize: 17 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", lineHeight: 1.2 }}>
                                Monthly Attendance Summary
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                                {MONTHS[month - 1]} {year} &middot; Attendance KPIs & Analytics
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Grid - Dropdowns allow floating overlays without clipping */}
                <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                    <Col xs={12} sm={6}>
                        <Select
                            value={organisationId}
                            onChange={(v) => setOrganisationId(v || "ALL")}
                            showSearch
                            optionFilterProp="children"
                            allowClear
                            onClear={() => setOrganisationId("ALL")}
                            placeholder="Organisation"
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            <Option value="ALL">
                                <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>All Orgs ({organisations.length})</span></Space>
                            </Option>
                            {organisations.map(o => (
                                <Option key={o.id} value={o.id}>
                                    <Space size={4}><BankOutlined style={{ color: "#0e497b" }} /> <span>{o.name}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={12} sm={6}>
                        <Select
                            value={departmentId}
                            onChange={(v) => setDepartmentId(v || "ALL")}
                            showSearch
                            optionFilterProp="children"
                            allowClear
                            onClear={() => setDepartmentId("ALL")}
                            placeholder="Department"
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            <Option value="ALL">
                                <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>All Depts ({departments.length})</span></Space>
                            </Option>
                            {departments.map(d => (
                                <Option key={d.id} value={d.id}>
                                    <Space size={4}><AppstoreOutlined style={{ color: "#0e497b" }} /> <span>{d.name}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={12} sm={6}>
                        <Select
                            value={month}
                            onChange={setMonth}
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            {MONTHS.map((m, i) => (
                                <Option key={i + 1} value={i + 1}>
                                    <Space size={4}><CalendarOutlined style={{ color: "#2563eb" }} /> <span>{m}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={12} sm={6}>
                        <Select
                            value={year}
                            onChange={setYear}
                            style={{ width: "100%" }}
                            size="middle"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentElement}
                        >
                            {YEARS.map(y => (
                                <Option key={y} value={y}>
                                    <Space size={4}><FilterOutlined style={{ color: "#7c3aed" }} /> <span>{y}</span></Space>
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                {loading ? (
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>
                            Loading Attendance Summary...
                        </div>
                    </div>
                ) : !data ? (
                    <Empty description="No attendance summary data available" style={{ padding: "40px 0" }} />
                ) : (
                    <>
                        {/* KPI Cards — 2 rows x 4 cards */}
                        <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
                            {kpis.map(k => (
                                <Col xs={12} sm={12} md={6} lg={6} key={k.label}>
                                    <div style={{
                                        background: k.bg, border: `1px solid ${k.border}`,
                                        borderRadius: 12, padding: "10px 12px", height: "100%", minHeight: 96,
                                        display: "flex", flexDirection: "column", justifyContent: "space-between",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
                                    }}>
                                        <div>
                                            <div style={{
                                                display: "flex", alignItems: "center", gap: 5,
                                                marginBottom: 4,
                                            }}>
                                                <span style={{ color: k.color, fontSize: 13 }}>{k.icon}</span>
                                                <span style={{
                                                    fontSize: 10.5, fontWeight: 800, color: k.color,
                                                    textTransform: "uppercase", letterSpacing: "0.03em"
                                                }}>
                                                    {k.label}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 21, fontWeight: 900, color: k.color, lineHeight: 1.1 }}>
                                                {k.val}
                                            </div>
                                        </div>

                                        {k.showProgress && (
                                            <div style={{ marginTop: 6 }}>
                                                <Progress
                                                    percent={Math.min(k.progress, 100)}
                                                    strokeColor={k.color}
                                                    trailColor="#e2e8f0"
                                                    showInfo={false}
                                                    size={[undefined, 5]}
                                                    style={{ margin: 0 }}
                                                />
                                            </div>
                                        )}

                                        {k.subTags && (
                                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                                                {k.subTags.map(st => (
                                                    <Tag key={st.label} style={{
                                                        fontSize: 9.5, fontWeight: 700, margin: 0, padding: "1px 5px",
                                                        borderRadius: 4, color: st.color, border: `1px solid ${st.color}40`,
                                                        background: "#ffffff90"
                                                    }}>
                                                        {st.label}
                                                    </Tag>
                                                ))}
                                            </div>
                                        )}

                                        {k.subText && !k.showProgress && !k.subTags && (
                                            <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 4, fontWeight: 600 }}>
                                                {k.subText}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {/* Enhanced Recharts Visualization */}
                        {chartData.length > 0 && (
                            <div style={{
                                background: "#fafafa", borderRadius: 12, border: "1px solid #f1f5f9",
                                padding: "12px 12px 10px 12px", marginTop: 4
                            }}>
                                {/* Header with Segmented toggle */}
                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #eef2f6"
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <BarChartOutlined style={{ color: "#0e497b", fontSize: 13 }} />
                                        <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
                                            Attendance & Leave Breakdown
                                        </span>
                                    </div>
                                    <Segmented
                                        size="small"
                                        value={vizMode}
                                        onChange={setVizMode}
                                        options={[
                                            { label: "Rate (%)", value: "rate" },
                                            { label: "Count (Days)", value: "count" }
                                        ]}
                                    />
                                </div>

                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={chartData} margin={{ top: 18, right: 15, left: -20, bottom: 0 }} barSize={26}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fill: "#475569", fontSize: 10, fontWeight: 700 }}
                                            tickLine={false}
                                            axisLine={{ stroke: "#cbd5e1" }}
                                            interval={0}
                                        />
                                        <YAxis
                                            domain={vizMode === "rate" ? [0, 100] : [0, "auto"]}
                                            tick={{ fill: "#64748b", fontSize: 10 }}
                                            tickFormatter={v => vizMode === "rate" ? `${v}%` : (v >= 1000 ? `${(v/1000).toFixed(1)}k` : v)}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = payload[0]?.payload;
                                                if (!d) return null;
                                                return (
                                                    <div style={{
                                                        background: "rgba(15, 23, 42, 0.94)",
                                                        color: "#fff", padding: "8px 12px", borderRadius: 8,
                                                        fontSize: 12, boxShadow: "0 6px 18px rgba(0,0,0,0.22)",
                                                        border: "1px solid rgba(255,255,255,0.08)", minWidth: 150
                                                    }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, fontWeight: 800 }}>
                                                            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: d.fill }} />
                                                            <span>{d.name}</span>
                                                        </div>
                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11, color: "#cbd5e1" }}>
                                                            <span>Count:</span>
                                                            <b style={{ color: "#fff" }}>{Number(d.count || 0).toLocaleString()} Days</b>
                                                        </div>
                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11, color: "#cbd5e1" }}>
                                                            <span>Rate:</span>
                                                            <b style={{ color: d.fill }}>{d.rate}%</b>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                            <LabelList dataKey="label" position="top" style={{ fill: "#334155", fontSize: 10, fontWeight: 800 }} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>

                                {/* Badges showing Count and Rate for every category including Week Off & Comp Off */}
                                <div style={{
                                    display: "flex", flexWrap: "wrap", justifyContent: "center",
                                    gap: "6px 10px", marginTop: 8, paddingTop: 8, borderTop: "1px dashed #e2e8f0"
                                }}>
                                    {chartData.map(item => (
                                        <div
                                            key={item.shortName}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 5,
                                                fontSize: 10.5, color: "#475569", background: "#ffffff",
                                                border: "1px solid #e2e8f0", padding: "2px 7px", borderRadius: 6
                                            }}
                                        >
                                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: item.fill, flexShrink: 0 }} />
                                            <span style={{ fontWeight: 600 }}>{item.shortName}:</span>
                                            <span style={{ fontWeight: 800, color: "#0f172a" }}>
                                                {Number(item.count || 0).toLocaleString()}&nbsp;
                                                <span style={{ color: "#64748b", fontWeight: 500 }}>({item.rate}%)</span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}


